import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

const API = process.env.REACT_APP_BACKEND_URL;

// Create context
const RealtimeContext = createContext(null);

// Provider component
export const RealtimeProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [roomUsers, setRoomUsers] = useState([]);
  const [locks, setLocks] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Initialize socket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    
    if (!token || !user) return;

    // Connect to Socket.IO server
    const socketInstance = io(API, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token }
    });

    // Connection events
    socketInstance.on('connect', () => {
      console.log('Socket connected:', socketInstance.id);
      setIsConnected(true);
      reconnectAttempts.current = 0;
      
      // Authenticate
      socketInstance.emit('authenticate', {
        email: user.email,
        name: user.full_name || user.email,
        user_id: user.id,
        tenant_id: user.email,
        role: user.role || 'viewer'
      });
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      reconnectAttempts.current++;
      if (reconnectAttempts.current >= maxReconnectAttempts) {
        console.log('Max reconnect attempts reached');
        socketInstance.disconnect();
      }
    });

    // Presence events
    socketInstance.on('presence_update', (data) => {
      setRoomUsers(data.users || []);
    });

    socketInstance.on('room_joined', (data) => {
      setCurrentRoom(data.room_id);
      setRoomUsers(data.users || []);
    });

    // Lock events
    socketInstance.on('resource_locked', (data) => {
      setLocks(prev => ({
        ...prev,
        [data.resource_id]: {
          locked_by: data.locked_by,
          locked_at: data.locked_at
        }
      }));
    });

    socketInstance.on('lock_released', (data) => {
      setLocks(prev => {
        const newLocks = { ...prev };
        delete newLocks[data.resource_id];
        return newLocks;
      });
    });

    socketInstance.on('lock_acquired', (data) => {
      console.log('Lock acquired:', data.resource_id);
    });

    socketInstance.on('lock_denied', (data) => {
      console.log('Lock denied:', data);
      // Show notification
      addNotification({
        type: 'warning',
        message: `This item is being edited by ${data.locked_by}`,
        timestamp: new Date().toISOString()
      });
    });

    // Data update events
    socketInstance.on('data_update', (data) => {
      console.log('Data update received:', data.type);
      // Trigger custom event for components to listen to
      window.dispatchEvent(new CustomEvent('realtime_data_update', { detail: data }));
    });

    // Notification events
    socketInstance.on('notification', (data) => {
      addNotification(data);
    });

    // Typing indicators
    socketInstance.on('user_typing', (data) => {
      if (data.is_typing) {
        setTypingUsers(prev => ({
          ...prev,
          [data.field_id]: { ...data, timestamp: Date.now() }
        }));
      } else {
        setTypingUsers(prev => {
          const newTyping = { ...prev };
          delete newTyping[data.field_id];
          return newTyping;
        });
      }
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // Clean up stale typing indicators
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => {
        const filtered = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (now - value.timestamp < 3000) {
            filtered[key] = value;
          }
        });
        return filtered;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Add notification
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, 10));
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n !== notification));
    }, 5000);
  }, []);

  // Join room (page)
  const joinRoom = useCallback((roomId) => {
    if (socket && isConnected) {
      socket.emit('join_room', { room_id: roomId });
    }
  }, [socket, isConnected]);

  // Leave room
  const leaveRoom = useCallback((roomId) => {
    if (socket && isConnected) {
      socket.emit('leave_room', { room_id: roomId });
      setCurrentRoom(null);
      setRoomUsers([]);
    }
  }, [socket, isConnected]);

  // Acquire lock on resource
  const acquireLock = useCallback((resourceId, resourceType = 'unknown') => {
    if (socket && isConnected) {
      socket.emit('acquire_lock', { resource_id: resourceId, resource_type: resourceType });
    }
  }, [socket, isConnected]);

  // Release lock
  const releaseLock = useCallback((resourceId) => {
    if (socket && isConnected) {
      socket.emit('release_lock', { resource_id: resourceId });
    }
  }, [socket, isConnected]);

  // Check if resource is locked by someone else
  const isLockedByOther = useCallback((resourceId) => {
    return !!locks[resourceId];
  }, [locks]);

  // Get lock info
  const getLockInfo = useCallback((resourceId) => {
    return locks[resourceId] || null;
  }, [locks]);

  // Typing indicators
  const startTyping = useCallback((fieldId) => {
    if (socket && isConnected && currentRoom) {
      socket.emit('typing_start', { room_id: currentRoom, field_id: fieldId });
    }
  }, [socket, isConnected, currentRoom]);

  const stopTyping = useCallback((fieldId) => {
    if (socket && isConnected && currentRoom) {
      socket.emit('typing_stop', { room_id: currentRoom, field_id: fieldId });
    }
  }, [socket, isConnected, currentRoom]);

  // Get who's typing in a field
  const getTypingUser = useCallback((fieldId) => {
    return typingUsers[fieldId] || null;
  }, [typingUsers]);

  // Dismiss notification
  const dismissNotification = useCallback((index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  }, []);

  const value = {
    isConnected,
    currentRoom,
    roomUsers,
    notifications,
    joinRoom,
    leaveRoom,
    acquireLock,
    releaseLock,
    isLockedByOther,
    getLockInfo,
    startTyping,
    stopTyping,
    getTypingUser,
    dismissNotification
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

// Custom hook to use realtime context
export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    // Return a mock object if not in provider (for backwards compatibility)
    return {
      isConnected: false,
      currentRoom: null,
      roomUsers: [],
      notifications: [],
      joinRoom: () => {},
      leaveRoom: () => {},
      acquireLock: () => {},
      releaseLock: () => {},
      isLockedByOther: () => false,
      getLockInfo: () => null,
      startTyping: () => {},
      stopTyping: () => {},
      getTypingUser: () => null,
      dismissNotification: () => {}
    };
  }
  return context;
};

// Presence indicator component
export const PresenceIndicator = ({ className = '' }) => {
  const { isConnected, roomUsers } = useRealtime();
  
  if (!isConnected || roomUsers.length === 0) return null;
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex -space-x-2">
        {roomUsers.slice(0, 3).map((user, idx) => (
          <div 
            key={user.email} 
            className="w-8 h-8 rounded-full bg-indigo-500 border-2 border-white flex items-center justify-center text-white text-xs font-medium"
            title={user.name || user.email}
          >
            {(user.name || user.email).charAt(0).toUpperCase()}
          </div>
        ))}
        {roomUsers.length > 3 && (
          <div className="w-8 h-8 rounded-full bg-slate-400 border-2 border-white flex items-center justify-center text-white text-xs font-medium">
            +{roomUsers.length - 3}
          </div>
        )}
      </div>
      <span className="text-sm text-slate-600">{roomUsers.length} online</span>
    </div>
  );
};

// Lock indicator component
export const LockIndicator = ({ resourceId, className = '' }) => {
  const { getLockInfo } = useRealtime();
  const lockInfo = getLockInfo(resourceId);
  
  if (!lockInfo) return null;
  
  return (
    <div className={`flex items-center gap-1 text-amber-600 ${className}`}>
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
      </svg>
      <span className="text-xs">{lockInfo.locked_by} is editing</span>
    </div>
  );
};

// Typing indicator component
export const TypingIndicator = ({ fieldId, className = '' }) => {
  const { getTypingUser } = useRealtime();
  const typingUser = getTypingUser(fieldId);
  
  if (!typingUser) return null;
  
  return (
    <div className={`flex items-center gap-1 text-slate-500 ${className}`}>
      <span className="text-xs">{typingUser.user_name || 'Someone'} is typing</span>
      <span className="animate-pulse">...</span>
    </div>
  );
};

// Notification toast container
export const NotificationToasts = () => {
  const { notifications, dismissNotification } = useRealtime();
  
  if (notifications.length === 0) return null;
  
  return (
    <div className="fixed bottom-4 right-4 space-y-2 z-50">
      {notifications.map((notification, idx) => (
        <div 
          key={idx}
          className={`p-4 rounded-lg shadow-lg max-w-sm flex items-start gap-3 ${
            notification.type === 'error' ? 'bg-red-50 text-red-800' :
            notification.type === 'warning' ? 'bg-amber-50 text-amber-800' :
            notification.type === 'success' ? 'bg-green-50 text-green-800' :
            'bg-blue-50 text-blue-800'
          }`}
        >
          <div className="flex-1">
            <p className="text-sm font-medium">{notification.message}</p>
            {notification.from?.name && (
              <p className="text-xs opacity-75 mt-1">From: {notification.from.name}</p>
            )}
          </div>
          <button 
            onClick={() => dismissNotification(idx)}
            className="text-slate-400 hover:text-slate-600"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default RealtimeContext;
